import React from "react";
import { CssBaseline, StyledEngineProvider, ThemeProvider as MUIProvider } from "@mui/material";
import { alpha, createTheme, darken, lighten, SimplePaletteColorOptions, Theme } from "@mui/material/styles";
import { useBrandColors } from "../../brand/hooks";
import muiThemeConfig from "./muiThemeConfig";

declare module "@mui/material/styles/createPalette" {
  interface Palette {
    tertiary: PaletteColor;
  }
  interface PaletteOptions {
    tertiary: PaletteColorOptions;
  }
}

declare module "@mui/styles/defaultTheme" {
  interface DefaultTheme extends Theme {}
}

export default function MUIThemeProvider({ children }: React.PropsWithChildren) {
  const brandedTheme = useBrandedMuiTheme();
  return (
    <StyledEngineProvider injectFirst>
      <CssBaseline />
      <MUIProvider theme={brandedTheme}>{children}</MUIProvider>
    </StyledEngineProvider>
  );
}

export function useBrandedMuiTheme() {
  const colors = useBrandColors();

  const brandedPalette = {
    ...muiThemeConfig?.palette,
    primary: {
      main: colors?.primary,
      light: lighten(colors?.primary, 0.5),
      dark: darken(colors?.primary, 0.2),
      contrastText: colors?.primary_text,
    },
    secondary: {
      main: colors?.secondary,
      light: lighten(colors?.secondary, 0.5),
      dark: darken(colors?.secondary, 0.2),
      contrastText: colors?.secondary_text,
    },
    tertiary: {
      main: colors?.tertiary,
      light: lighten(colors?.tertiary, 0.5),
      dark: darken(colors?.tertiary, 0.35),
      contrastText: colors?.tertiary_text,
    },
  };

  const components = {
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&.Mui-selected, &.Mui-selected:hover": {
            color: brandedPalette?.primary?.contrastText,
            backgroundColor: brandedPalette?.primary?.main,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            opacity: 0.5,
          },
        },
      },
    },
    MUIDataTableSelectCell: {
      styleOverrides: {
        root: {
          backgroundColor: muiThemeConfig.palette?.background?.paper,
        },
      },
    },
    MUIDataTableBodyCell: {
      styleOverrides: {
        root: {
          "& button.Mui-disabled": {
            opacity: 0.5,
          },
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          "& .datatables-noprint": {
            margin: "100px 0",
            marginLeft: "40%",
            maxWidth: "fit-content",
          },
        },
      },
    },
    MUIDataTableToolbar: {
      styleOverrides: {
        actions: {
          "& [aria-label='View Columns']": {
            "&:after": {
              content: '"Columns"',
              fontSize: 14,
              position: "relative",
              left: "5px",
              color: brandedPalette?.primary?.main,
              paddingRight: "5px",
            },
          },
          "& button": {
            color: brandedPalette?.primary?.main,
            padding: "5px 10px",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: brandedPalette?.primary?.light,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: any }) => {
          const severityColor = brandedPalette?.[ownerState.severity as "success" | "error" | "warning" | "info"] as
            | SimplePaletteColorOptions
            | undefined;
          return {
            // Floating messages (snackbars) use the filled variant — same light tint + dark
            // text as the inline alerts (saturated severity fills are unreadable), plus a
            // shadow so they lift off the page as overlays.
            ...(ownerState.variant === "filled" &&
              severityColor && {
                backgroundColor: severityColor.light,
                color: brandedPalette?.text?.primary,
                ".MuiAlert-icon": {
                  color: severityColor.main,
                },
                fontWeight: 500,
                boxShadow: "0px 8px 12px -3px rgba(16,24,40,0.14), 0px 4px 6px -4px rgba(16,24,40,0.10)",
              }),
            // Inline info boxes use the standard variant — give every severity a visible tint
            // and hairline border so they don't disappear against white cards.
            ...(ownerState.variant === "standard" &&
              severityColor && {
                backgroundColor: severityColor.light,
                color: brandedPalette?.text?.primary,
                border: `1px solid ${alpha(severityColor.main as string, 0.3)}`,
                ".MuiAlert-icon": {
                  color: severityColor.main,
                },
              }),
            ...(ownerState.severity === "success" &&
              ownerState.variant === "standard" && {
                color: (brandedPalette?.success as SimplePaletteColorOptions)?.dark,
              }),
          };
        },
      },
    },
  };

  return createTheme({
    ...muiThemeConfig,
    palette: brandedPalette,
    components: {
      ...muiThemeConfig.components,
      ...components,
    },
  });
}
